import React, { FC, useEffect, useRef, useState } from 'react';
import { KeyboardAvoidingView, Text, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import ColorWrapper, { WithColor } from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { Alert, convertTimeLetter, isPhone, navigateBack } from '@helpers';
import {
  GlobalSearchItem,
  NCFileData,
  ProductionOrderSearchItem,
  RecipeSearchItem,
  TimeLetter,
  ProductionBatchFunction,
} from '../../../interfaces/search';

import { addProductionInformationV2, getNCFileById } from '@reducers/manufacture';
import { globalSearch, partsSearch, productionOrdersSearch, recipesSearch, getProductionBatchStatus } from '@reducers/search';

import SearchInput from '@components/common/searchInput';
import TouchableElement from '@components/common/touchableElement';

import Header from '@components/productionInformation/components/Header';
import { generateImageURL } from '@reducers/blobStorage';
import { SmallLoading } from '@components/loading';
import ProductionOrderBlock from './components/ProductionOrderBlock';
import RecipeBlock from './components/RecipeBlock';
import PartBlock from './components/PartBlock';
import LineMultimediaButton from './components/LineMultimediaButton';
import NCFile from '@components/productionInformation/components/NCFile';
import Context from './components/Context';
import { modalBg, modalFieldBg } from '@v2/pages/common/constants/base.constants';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ColorFunction } from '@styles/colors/types';
import { SEARCH_PAGE_SIZE } from './constants';
import { SelectedLevel } from './types';
import { GenerateImageURL } from '@interfaces/methods';

let searchTimeout: NodeJS.Timeout;

interface BaseWithERPProps extends WithColor {
  partId?: number;
  existingSelectedLevel?: SelectedLevel;
  route: any;
  onBackPress: () => void;
  hide: () => void;
  getNCFileById: any;
  generateImageURL: GenerateImageURL;
  globalSearch: any;
  productionOrdersSearch: any;
  recipesSearch: any;
  partsSearch: any;
  addProductionInformationV2: any;
  productionBatchId?: string;
  selectedNCFileId: number;
  getProductionBatchStatus: (id: string) => ProductionBatchFunction;
  isOperatorPanel?: boolean;
  title?: string;
  isNcFileContentAvailable?: boolean;
  shouldTakeNCFileDataFromMachine?: boolean;
  onActionStart?: () => void;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const BaseWithERP: FC<BaseWithERPProps> = ({
  C,
  partId,
  existingSelectedLevel,
  getNCFileById,
  generateImageURL,
  route,
  onBackPress,
  hide = navigateBack,
  globalSearch,
  productionOrdersSearch,
  recipesSearch,
  partsSearch,
  addProductionInformationV2,
  productionBatchId,
  getProductionBatchStatus,
  selectedNCFileId,
  isOperatorPanel,
  title,
  isNcFileContentAvailable,
  shouldTakeNCFileDataFromMachine,
  onActionStart,
  scale = initialScale,
  font = initialFont,
}) => {
  const [data, setData] = useState<NCFileData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchResult, setSearchResult] = useState<GlobalSearchItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<GlobalSearchItem | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<SelectedLevel | null>(existingSelectedLevel || null);
  const [productionOrder, setProductionOrder] = useState<ProductionOrderSearchItem | undefined>();
  const [recipe, setRecipe] = useState<RecipeSearchItem | undefined>();
  const [part, setPart] = useState<GlobalSearchItem | undefined | null>();
  const [historyPool, setHistoryPool] = useState<any>([]);
  const [selectedPartId, setSelectedPartId] = useState<number | undefined>(partId);

  const searchRef = useRef<any>(null);
  const abortController = useRef<any>();
  const canLoadMore = useRef(false);
  const hasMore = useRef(true);

  const showCancel = searchValue || isFocused;
  const isBatch = !isLoading && data?.productionBatchId;

  useEffect(() => {
    if (!selectedLevel && selectedPartId != null) {
      setSelectedLevel(SelectedLevel.Part);
    }
  }, [selectedPartId, selectedLevel]);

  const handleItemInfoPress = () => {
    console.log('INFO');
  };

  const handleCancelPress = () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    handleSearch('');
  };

  const handleSearch = async (v: string, loadMore = false) => {
    setIsSearching(true);
    setSearchValue(v);

    const oDataFilter = `ItemType eq 'Part'${isBatch ? '' : " or ItemType eq 'Recipe'"} or ItemType eq 'ProductionOrder'`;

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const skip = loadMore ? currentSkip : 0;

    if (!loadMore) {
      hasMore.current = true;
      canLoadMore.current = false;
    }

    if (abortController.current) {
      abortController.current.abort();
    }

    abortController.current = new AbortController();
    const signal = abortController.current.signal;

    searchTimeout = setTimeout(async () => {
      const res = await globalSearch({
        data: {
          search: v,
          top: SEARCH_PAGE_SIZE,
          skip,
        },
        filter: { oDataFilter, orderBy: ['ScheduledStartDate asc'] },
        signal,
      });

      if (res?.searchResult) {
        if (loadMore) {
          setSearchResult((prevResults) => [...prevResults, ...res.searchResult]);
        } else {
          setSearchResult(res.searchResult);
        }
        setCurrentSkip(skip + res.searchResult.length);
        hasMore.current = res.searchResult.length === SEARCH_PAGE_SIZE;
      } else {
        setIsError(true);
      }
      setIsSearching(false);
    }, 300);
  };

  const handlePressItem = (item: GlobalSearchItem | null) => {
    setSelectedItem(item);
    console.log(item, 'item');
    setHistoryPool([...historyPool, { resetSelectedLevel: true }]);

    switch (item?.itemType) {
      case 'ProductionOrder':
        return setSelectedLevel(SelectedLevel.ProductionOrder);
      case 'Recipe':
        return setSelectedLevel(SelectedLevel.Recipe);
      case 'Part':
        return setSelectedLevel(SelectedLevel.Part);
      default:
        return setSelectedLevel(null);
    }
  };

  const handleBackPress = () => {
    const last = historyPool[historyPool.length - 1];
    if (last) {
      if (last.resetRecipe) {
        setRecipe(undefined);
      }
      if (last.resetProductionOrder) {
        setProductionOrder(undefined);
      }
      if (last.resetPart) {
        setPart(undefined);
      }
      if (last.resetSelectedLevel) {
        setSelectedLevel(null);
      }
      if (Object.keys(last).includes('resetLevelTo')) {
        setSelectedLevel(last.resetLevelTo);
      }
      setHistoryPool(historyPool.slice(0, -1));
    } else {
      if (onBackPress) {
        return onBackPress();
      }

      if (hide) {
        return hide();
      }
    }
  };

  const formatTimeToSeconds = (t: TimeLetter) => Number(t.h) * 60 * 60 + Number(t.m) * 60 + Number(t.s);

  const handleLink = async ({
    partId,
    productionOrderId,
    productionOperationId,
    recipeId,
    recipeOperationId,
  }: {
    partId?: number;
    productionOrderId?: number;
    productionOperationId?: number;
    recipeId?: number;
    recipeOperationId?: number;
  }) => {
    if (data) {
      setIsLoading(true);
      if (onActionStart) {
        onActionStart();
      }
      const response = await addProductionInformationV2({
        ncFileId: data.ncFileId,
        clampingDetails: {
          runTimePerPcsSecond: formatTimeToSeconds(data.ideal.runtime),
          changeTimePerPcsSecond: formatTimeToSeconds(data.ideal.changeTime),
          setupTimeSecond: formatTimeToSeconds(data.ideal.setupTime),
        },
        ...(partId && { partId }),
        ...(productionOrderId && { productionOrderId }),
        ...(productionOperationId && { productionOperationId }),
        ...(productionBatchId && { productionBatchId }),
        ...(recipeId && { recipeId }),
        ...(recipeOperationId && { recipeOperationId }),
      });

      if (response.error) {
        Alert({ title: response?.errorDetails?.errorMessage || 'Something went wrong', type: 'danger' });
      } else {
        hide();
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleSearch('');
  }, []);

  useEffect(() => {
    (async () => {
      if (selectedNCFileId) {
        setIsLoading(true);
        const date = { h: '0', m: '0', s: '0' };

        const ncFile = await getNCFileById(selectedNCFileId);

        let batch: any = null;
        if (productionBatchId) {
          const response = await getProductionBatchStatus(productionBatchId);
          batch = response?.searchResult?.[0];
        }

        if (ncFile) {
          setSelectedPartId(ncFile?.partId);
          setData({
            ncFileId: ncFile.ncFileId,
            ncFileName: ncFile.ncFileName,
            ncFileStatus: ncFile.ncFileStatus,
            versions: ncFile.versions,
            part: {
              partId: ncFile.partId,
              partDrawingPosition: ncFile.partDrawingPosition,
              partNumber: ncFile.partNumber,
              partDrawingNumber: ncFile.partDrawingNumber,
              partDescription: ncFile.partDescription,
              partDrawingRevision: ncFile.partDrawingRevision,
            },
            ideal: {
              timeType: 'manual',
              runtime: convertTimeLetter(ncFile.runTimePerPcsSecond) || date,
              changeTime: convertTimeLetter(ncFile.changeTimePerPcsSecond) || date,
              setupTime: convertTimeLetter(ncFile.setupTimeSecond) || date,
            },
            paid: {
              timeType: 'manual',
              runtime: date,
              changeTime: date,
              setupTime: date,
            },
            batch,
            productionBatchId,
          });

          // if (batch.links.length) {
          //   setSelectedLevel('ProductionOrderOrRecipe');
          //   setRecipe(undefined);
          //   setProductionOrder(batch.links[batch.links.length - 1]);
          //   setSelectedItem({ itemId: file.partId });
          //   setIsLoading(false);
          //   return;
          // }

          setIsLoading(false);
        } else {
          return onBackPress();
        }
      }
    })();
  }, []);

  const renderContent = () => {
    switch (selectedLevel) {
      case SelectedLevel.ProductionOrder:
        return (
          <ProductionOrderBlock
            {...{
              C,
              ncFileName: data?.ncFileName,
              existingProductionOrder: productionOrder,
              existingPart: part,
              item: selectedItem,
              hideMoveHistoryBackButton: existingSelectedLevel === SelectedLevel.ProductionOrder,
              handleLink,
              setHistoryPool,
              setSelectedLevel,
              scale,
              font,
            }}
          />
        );
      case SelectedLevel.Recipe:
        return (
          <RecipeBlock
            {...{
              C,
              ncFileName: data?.ncFileName,
              existingRecipe: recipe,
              existingPart: part,
              item: selectedItem,
              hideMoveHistoryBackButton: existingSelectedLevel === SelectedLevel.Recipe,
              handleLink,
              setHistoryPool,
              setSelectedLevel,
              scale,
              font,
            }}
          />
        );
      case SelectedLevel.Part:
        return (
          <PartBlock
            {...{
              C,
              setProductionOrder,
              setRecipe,
              part,
              setPart,
              setSelectedLevel,
              ncFileName: data?.ncFileName,
              item: selectedItem,
              partId: selectedPartId,
              handleItemInfoPress,
              generateImageURL,
              productionOrdersSearch,
              recipesSearch,
              partsSearch,
              handleLink,
              historyPool,
              setHistoryPool,
              isBatch,
              hideMoveHistoryBackButton: existingSelectedLevel === SelectedLevel.Part || selectedPartId != null,
              scale,
              font,
            }}
          />
        );
      default:
        return (
          <>
            <View style={{ flexDirection: 'row', paddingHorizontal: scale(18) }}>
              <SearchInput
                {...{
                  C,
                  searchValue,
                  onFocus: () => {
                    setIsFocused(true);
                  },
                  searchRef,
                  onSearch: handleSearch,
                  inputName: `Parts, Drawings${isBatch ? '' : ', Recipes'} or Production Orders to link to`,
                  onBlur: () => setIsFocused(false),
                  bg: modalFieldBg,
                  shadow: true,
                  scale,
                  font,
                }}
              />
              {showCancel && (
                <TouchableElement
                  onPress={() => {
                    if (searchRef.current) {
                      searchRef.current.blur();
                      handleCancelPress();
                    }
                  }}
                  hitSlop={5}
                >
                  {({ boxStyle, pressHoverColor }) => (
                    <View
                      style={[
                        boxStyle,
                        {
                          paddingLeft: scale(16),
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
                    </View>
                  )}
                </TouchableElement>
              )}
            </View>
            <View style={{ marginTop: scale(20), flex: 1 }}>
              <FlashList
                data={searchResult}
                keyExtractor={(item) => `${item.itemId}-${item.itemType}`}
                contentContainerStyle={{ paddingBottom: scale(15), paddingHorizontal: scale(18) }}
                onEndReachedThreshold={0.5}
                onScroll={() => {
                  canLoadMore.current = true;
                }}
                onEndReached={() => {
                  if (canLoadMore.current && hasMore.current && !isSearching && searchResult.length) {
                    canLoadMore.current = false;
                    handleSearch(searchValue, true);
                  }
                }}
                renderItem={({ item, index }) => (
                  <LineMultimediaButton
                    {...{
                      C,
                      onPress: () => handlePressItem(item),
                      documentData: item.documentData,
                      generateImageURL,
                      height: 66,
                      title: item.itemDescription,
                      highlighted: item.highlightedText,
                      text: item.itemMemo,
                      additionalText: item.itemType,
                      onAdditionalBtnPress: handleItemInfoPress,
                      additionalBtnIcon: 'info',
                      bg: modalFieldBg,
                      borderRadiusTop: index === 0,
                      borderRadiusBottom: index === searchResult.length - 1,
                      borderBottom: index !== searchResult.length - 1,
                      shadow: false,
                      scale,
                      font,
                    }}
                  />
                )}
              />
            </View>
          </>
        );
    }
  };

  return (
    <View
      testID={TEST_IDS.popups.linkBatchWithERP}
      style={{
        width: '100%',
        flex: 1,
        backgroundColor: C(modalBg),
        alignItems: 'center',
        // minHeight: scale(790),
      }}
    >
      <View style={{ width: '100%', flex: 1 }}>
        <Header
          {...{
            C,
            onBackPress: handleBackPress,
            title: title ?? `Link ${isBatch ? 'Batch' : 'NC File'} to Production Information`,
            rightAction: hide,
            rightActionText: 'Close',
            scale,
            font,
          }}
        />
        <View style={{ width: '100%', flex: 1, flexDirection: 'row', justifyContent: 'center', marginTop: scale(10) }}>
          {!isPhone && (
            <View
              style={{
                width: '50%',
                paddingLeft: scale(18),
              }}
            >
              {!isLoading && <Context {...{ data, C, scale, font }} />}
              <NCFile
                {...{
                  C,
                  selectedItem: { machineStatus: data },
                  showNcFileButtonCallBackOnPress: hide,
                  ncFileId: data?.ncFileId,
                  ncFileName: data?.ncFileName,
                  isOperatorPanel,
                  isNcFileContentAvailable,
                  shouldTakeNCFileDataFromMachine,
                  scale,
                  font,
                }}
              />
            </View>
          )}
          <View
            style={{
              flex: 1,
            }}
          >
            <View
              style={{
                width: '100%',
                height: '100%',
                flex: 1,
              }}
            >
              {isLoading ? (
                <View style={{ flex: 1, justifyContent: 'center' }}>
                  <SmallLoading />
                </View>
              ) : (
                <View style={{ width: '100%', height: '100%', flexDirection: 'column', flex: 1 }}>{renderContent()}</View>
              )}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export default connect(({}) => ({}), {
  getNCFileById,
  globalSearch,
  productionOrdersSearch,
  recipesSearch,
  generateImageURL,
  partsSearch,
  addProductionInformationV2,
  getProductionBatchStatus,
})(ColorWrapper(BaseWithERP));
